import React, { useMemo, useState, useCallback } from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { DashboardDatePicker } from './DashboardDatePicker';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { chartPalette } from './chartPalette';

export interface StockByLocationDataPoint {
  locationId: string;
  locationName: string;
  disponible: number;
}

type DashboardMode = 'light' | 'dark';

interface StockAvailableByLocationChartProps {
  data?: StockByLocationDataPoint[];
  loading?: boolean;
  mode?: DashboardMode;
}

const ChartContainer = styled(Paper, { shouldForwardProp: (p) => p !== 'mode' })<{
  mode?: DashboardMode;
}>(({ mode = 'dark' }) => {
  const isLight = mode === 'light';
  return {
    padding: '24px',
    borderRadius: '16px',
    position: 'relative',
    overflow: 'hidden',
    ...(isLight
      ? {
          background: '#ffffff',
          border: '1px solid #bae6fd',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          '&::before': { content: 'none' },
        }
      : {
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          background:
            'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.05) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }),
  };
});

const HeaderContainer = styled(Box)({
  marginBottom: '24px',
  position: 'relative',
  zIndex: 1,
});

const Title = styled(Typography, { shouldForwardProp: (p) => p !== 'mode' })<{
  mode?: DashboardMode;
}>(({ mode = 'dark' }) => ({
  fontSize: '20px',
  fontWeight: 600,
  color: mode === 'light' ? '#0f172a' : '#ffffff',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  letterSpacing: '-0.01em',
}));

const BAR_COLORS = chartPalette.series;

const CustomTooltip = ({ active, payload, isLight }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Box
        sx={
          isLight
            ? {
                background: '#ffffff',
                border: '1px solid #e0f2fe',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }
            : {
                background:
                  'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
                padding: '12px 16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                backdropFilter: 'blur(10px)',
              }
        }
      >
        <Typography
          sx={{
            fontSize: '13px',
            fontWeight: 600,
            color: isLight ? '#64748b' : '#ffffff',
            marginBottom: '6px',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {data.locationName}
        </Typography>
        <Typography
          sx={{
            fontSize: '15px',
            fontWeight: 600,
            color: chartPalette.accent.teal,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {data.disponible.toLocaleString('es-BO')} piezas disponibles
        </Typography>
      </Box>
    );
  }
  return null;
};

const todayNormalized = (): Date => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const StockAvailableByLocationChart: React.FC<StockAvailableByLocationChartProps> = ({
  data,
  loading = false,
  mode = 'dark',
}) => {
  const isLight = mode === 'light';
  const gridStroke = isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.05)';
  const axisStroke = isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.4)';
  const tickFill = isLight ? '#64748b' : 'rgba(255, 255, 255, 0.6)';
  const labelFill = isLight ? '#1e293b' : '#ffffff';

  const [asOfDate, setAsOfDate] = useState<Date>(() => todayNormalized());
  const [datePickerAnchor, setDatePickerAnchor] = useState<HTMLElement | null>(null);

  const chartData = useMemo(() => data ?? [], [data]);

  const handleDateChange = useCallback((date: Date) => {
    setAsOfDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
  }, []);

  const openDatePicker = useCallback((e: React.MouseEvent<HTMLElement>) => {
    setDatePickerAnchor(e.currentTarget);
  }, []);

  const closeDatePicker = useCallback(() => {
    setDatePickerAnchor(null);
  }, []);

  const totalDisponible = useMemo(
    () => chartData.reduce((sum, item) => sum + item.disponible, 0),
    [chartData],
  );

  if (loading) {
    return (
      <ChartContainer mode={mode}>
        <Box
          sx={{ height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography sx={{ color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.5)' }}>
            Cargando...
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  if (chartData.length === 0) {
    return (
      <ChartContainer mode={mode}>
        <HeaderContainer
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Title mode={mode}>Stock disponible por sucursal/almacén</Title>
          <Button
            size="small"
            variant="outlined"
            onClick={openDatePicker}
            sx={
              isLight
                ? {
                    color: '#06b6d4',
                    borderColor: '#bae6fd',
                    textTransform: 'none',
                    fontSize: '13px',
                    '&:hover': { borderColor: '#38bdf8', bgcolor: 'rgba(6, 182, 212, 0.06)' },
                  }
                : {
                    color: 'rgba(255, 255, 255, 0.9)',
                    borderColor: 'rgba(255, 255, 255, 0.25)',
                    textTransform: 'none',
                    fontSize: '13px',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.45)',
                      backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    },
                  }
            }
          >
            {format(asOfDate, 'd MMM yyyy', { locale: es })}
          </Button>
        </HeaderContainer>
        <DashboardDatePicker
          value={asOfDate}
          onChange={handleDateChange}
          open={Boolean(datePickerAnchor)}
          anchorEl={datePickerAnchor}
          onClose={closeDatePicker}
        />
        <Box
          sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Typography sx={{ color: isLight ? '#94a3b8' : 'rgba(255, 255, 255, 0.5)' }}>
            No hay datos en el periodo seleccionado
          </Typography>
        </Box>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer mode={mode}>
      <HeaderContainer
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Title mode={mode}>Stock disponible por sucursal/almacén</Title>
        <Button
          size="small"
          variant="outlined"
          onClick={openDatePicker}
          sx={
            isLight
              ? {
                  color: '#06b6d4',
                  borderColor: '#bae6fd',
                  textTransform: 'none',
                  fontSize: '13px',
                  '&:hover': { borderColor: '#38bdf8', bgcolor: 'rgba(6, 182, 212, 0.06)' },
                }
              : {
                  color: 'rgba(255, 255, 255, 0.9)',
                  borderColor: 'rgba(255, 255, 255, 0.25)',
                  textTransform: 'none',
                  fontSize: '13px',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.45)',
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                  },
                }
          }
        >
          {format(asOfDate, 'd MMM yyyy', { locale: es })}
        </Button>
      </HeaderContainer>
      <DashboardDatePicker
        value={asOfDate}
        onChange={handleDateChange}
        open={Boolean(datePickerAnchor)}
        anchorEl={datePickerAnchor}
        onClose={closeDatePicker}
      />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={gridStroke}
              horizontal={true}
              vertical={false}
            />
            <XAxis
              type="number"
              stroke={axisStroke}
              style={{ fontSize: '12px', fontFamily: 'Inter, sans-serif' }}
              tick={{ fill: tickFill }}
              axisLine={{ stroke: axisStroke }}
              tickFormatter={(v) => v.toLocaleString('es-BO')}
            />
            <YAxis
              type="category"
              dataKey="locationName"
              stroke={axisStroke}
              style={{ fontSize: '13px', fontFamily: 'Inter, sans-serif' }}
              tick={{ fill: tickFill }}
              axisLine={{ stroke: axisStroke }}
              width={140}
              tickLine={false}
            />
            <Tooltip
              content={<CustomTooltip isLight={isLight} />}
              cursor={{ fill: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255, 255, 255, 0.05)' }}
              wrapperStyle={{ outline: 'none' }}
            />
            <Bar
              dataKey="disponible"
              radius={[0, 8, 8, 0]}
              animationDuration={800}
              animationEasing="ease-out"
              label={{
                position: 'right',
                fill: labelFill,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'Inter, sans-serif',
                formatter: (v: number) => v.toLocaleString('es-BO'),
              }}
            >
              {chartData.map((_, index) => (
                <Cell key={index} fill={BAR_COLORS[index % BAR_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box
        sx={
          isLight
            ? {
                marginTop: '16px',
                padding: '12px 16px',
                borderRadius: '8px',
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                textAlign: 'center',
                position: 'relative',
                zIndex: 1,
              }
            : {
                marginTop: '16px',
                padding: '12px 16px',
                borderRadius: '8px',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                textAlign: 'center',
                position: 'relative',
                zIndex: 1,
              }
        }
      >
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            color: isLight ? '#64748b' : 'rgba(255, 255, 255, 0.7)',
            fontFamily: 'Inter, sans-serif',
            marginBottom: '4px',
          }}
        >
          Total disponible
        </Typography>
        <Typography
          sx={{
            fontSize: '22px',
            fontWeight: 700,
            color: isLight ? '#0f172a' : '#ffffff',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {totalDisponible.toLocaleString('es-BO')} piezas
        </Typography>
      </Box>
    </ChartContainer>
  );
};
